import type { CSSProperties } from "react";
import { CHARTCOLORS, MODULE_ACCENT_SLOT_COUNT, chartColors } from "@/types/modules";

/**
 * How a module's `accent` reaches its colours. Grayscale always wins.
 *
 * Series colours: the accent rotates the five `--handyout-chart-1..5` slots so
 * that what slot 1 drew is drawn by slot `accent + 1` and the others shift
 * along. Unset and `0` both mean no rotation, so multi-series modules keep
 * today's colours until an author picks a different accent.
 *
 * Single marks (one brand-coloured icon or highlight): `--module-accent`,
 * which is `var(--primary)` while `accent` is unset and the chosen slot once
 * it is set, `0` included.
 */
export const MODULE_ACCENT_VAR = "--module-accent";

export function normalizeAccent(accent?: number | null): number | undefined {
  if (typeof accent !== "number" || !Number.isInteger(accent)) return undefined;
  if (accent < 0 || accent >= MODULE_ACCENT_SLOT_COUNT) return undefined;
  return accent;
}

export function accentOffset(accent?: number | null): number {
  return normalizeAccent(accent) ?? 0;
}

function slotVar(slot: number): string {
  return `var(--handyout-chart-${(slot % MODULE_ACCENT_SLOT_COUNT) + 1})`;
}

const OFFSETS = Array.from({ length: MODULE_ACCENT_SLOT_COUNT }, (_, offset) => offset);

const ROTATED_TOKENS: ReadonlyArray<CSSProperties | undefined> = OFFSETS.map((offset) => {
  if (offset === 0) return undefined;
  const style: Record<string, string> = {};
  for (let position = 0; position < MODULE_ACCENT_SLOT_COUNT; position++) {
    style[`--chart-${position + 1}`] = slotVar(offset + position);
  }
  return style as CSSProperties;
});

const ROTATED_CHART_COLORS: ReadonlyArray<string[]> = OFFSETS.map((offset) => {
  const slots = OFFSETS.map((position) =>
    offset === 0 && position === 0 ? "var(--primary)" : slotVar(offset + position),
  );
  return [...slots, ...CHARTCOLORS.slice(MODULE_ACCENT_SLOT_COUNT)];
});

/**
 * `--chart-1..5` rotation for a module root, or `undefined` when there is
 * nothing to rotate.
 */
export function accentTokenStyle(accent?: number | null, grayscale?: boolean): CSSProperties | undefined {
  if (grayscale) return undefined;
  return ROTATED_TOKENS[accentOffset(accent)];
}

/**
 * Accent-aware replacement for `chartColors()`: the five handyout slots in
 * rotated order followed by the remaining fallback colours. Without a rotation
 * the lead stays `var(--primary)`, which a palette scheme sets to slot 1, so an
 * author primary without a scheme still leads.
 */
export function accentChartColors(grayscale?: boolean, accent?: number | null): string[] {
  if (grayscale) return chartColors(true);
  return ROTATED_CHART_COLORS[accentOffset(accent)];
}

/**
 * The value of `--module-accent`: first mono rung under `grayscale`, the chosen
 * slot once `accent` is set, else the author's primary.
 */
export function accentMarkColor(grayscale?: boolean, accent?: number | null): string {
  return primaryToneVar(grayscale, accent) ?? "var(--primary)";
}

/**
 * Like `accentMarkColor`, but `undefined` in the unset case so a mark already
 * styled with a `--primary` class keeps rendering through that class alone.
 */
export function primaryToneVar(grayscale?: boolean, accent?: number | null): string | undefined {
  if (grayscale) return "var(--handyout-chart-mono-1)";
  const slot = normalizeAccent(accent);
  return slot === undefined ? undefined : slotVar(slot);
}

export function accentToneOr(
  grayscale: boolean | undefined,
  accent: number | null | undefined,
  fallback: string,
): string {
  return primaryToneVar(grayscale, accent) ?? fallback;
}

const MONO_TOKENS = Object.fromEntries(
  OFFSETS.map((position) => [`--chart-${position + 1}`, `var(--handyout-chart-mono-${position + 1})`]),
) as CSSProperties;

const SERIES_TOKENS: ReadonlyArray<CSSProperties> = OFFSETS.map(
  (offset) =>
    Object.fromEntries(
      OFFSETS.map((position) => [`--chart-${position + 1}`, slotVar(offset + position)]),
    ) as CSSProperties,
);

/**
 * `--chart-1..5` overrides for a module that cannot take the full
 * `importsTokenScope` because it reads `--muted`/`--border` with the app's
 * meaning: the non-chart mono ramp under `grayscale`, else the handyout slots,
 * rotated by the accent. The unrotated case is pinned too, so `var(--chart-N)`
 * never falls through to the app's unrelated `--chart-1..5`.
 */
export function seriesTokenStyle(grayscale?: boolean, accent?: number | null): CSSProperties {
  return grayscale ? MONO_TOKENS : SERIES_TOKENS[accentOffset(accent)];
}

const NEUTRAL_SLOT = 2;

const STATUS_TOKENS: ReadonlyArray<CSSProperties> = OFFSETS.map((offset) => {
  const chromatic = OFFSETS.map((position) => (offset + position) % MODULE_ACCENT_SLOT_COUNT).filter(
    (slot) => slot !== NEUTRAL_SLOT,
  );
  const style: Record<string, string> = { "--status-neutral": slotVar(NEUTRAL_SLOT) };
  chromatic.forEach((slot, index) => {
    style[`--status-${index + 1}`] = slotVar(slot);
  });
  return style as CSSProperties;
});

const STATUS_MONO_TOKENS = {
  "--status-1": "var(--handyout-chart-mono-1)",
  "--status-2": "var(--handyout-chart-mono-2)",
  "--status-3": "var(--handyout-chart-mono-4)",
  "--status-4": "var(--handyout-chart-mono-5)",
  "--status-neutral": "var(--handyout-chart-mono-3)",
} as CSSProperties;

/**
 * Semantic status colours for done/active/pending modes. `--status-neutral` is
 * always the unrotated quiet slot 3, so "pending" stays grey under any accent.
 * `--status-1..4` rotate over the four remaining slots only, starting at the
 * accent, so the lead status takes the accent colour and never the grey one.
 * Without an accent they equal `--chart-1`, `--chart-2`, `--chart-4`, `--chart-5`.
 */
export function statusTokenStyle(grayscale?: boolean, accent?: number | null): CSSProperties {
  return grayscale ? STATUS_MONO_TOKENS : STATUS_TOKENS[accentOffset(accent)];
}
